import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import ControlledDateRangePicker from 'shared/components/ControlledDateRangePicker';
import ShipBobPerformanceService from 'services/ShipBobPerformanceService';
import {
  ShipBobChannel,
  ShipBobPerformanceSummary,
  ProductCosts,
} from 'models/ShipBobPerformanceModel';
import SummaryCards from './components/SummaryCards';
import DailyTrendChart from './components/DailyTrendChart';
import ShipSpeedTables from './components/ShipSpeedTables';
import ZonesTable from './components/ZonesTable';
import ProductCostSection from './components/ProductCostSection';

const service = new ShipBobPerformanceService();

const PRESETS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 6 },
  { label: 'Last 14 days', days: 13 },
  { label: 'Last 30 days', days: 29 },
];

export default function ShipBobPerformancePage() {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjs().subtract(13, 'days'),
    dayjs(),
  ]);
  const [activePreset, setActivePreset] = useState<string>('Last 14 days');
  const [channelId, setChannelId] = useState<string>('');
  const [channels, setChannels] = useState<ShipBobChannel[]>([]);
  const [data, setData] = useState<ShipBobPerformanceSummary | null>(null);
  const [productCosts, setProductCosts] = useState<ProductCosts | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProductCostsLoading, setIsProductCostsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productCostsError, setProductCostsError] = useState(false);
  // Monotonic id per fetch; responses from a superseded fetch (range/channel
  // changed while in flight) are dropped instead of overwriting newer state.
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const [start, end] = dateRange;
    if (!start || !end) return;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setIsProductCostsLoading(true);
    setError(null);
    setProductCostsError(false);
    setProductCosts(null);
    const params = {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      ...(channelId ? { channelId } : {}),
    };
    // Independent requests, fired in parallel — the summary renders as soon
    // as it lands; the heavier product-costs query fills its section later.
    await Promise.all([
      service.getSummary(params).then(result => {
        if (fetchId !== fetchIdRef.current) return;
        if (result) {
          setData(result);
        } else {
          setError(
            'Failed to load ShipBob performance data. Check that the API is reachable.',
          );
        }
        setIsLoading(false);
      }),
      service.getProductCosts(params).then(costs => {
        if (fetchId !== fetchIdRef.current) return;
        setProductCosts(costs);
        setProductCostsError(!costs);
        setIsProductCostsLoading(false);
      }),
    ]);
  }, [dateRange, channelId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    service.getChannels().then(result => {
      if (result) setChannels(result);
    });
  }, []);

  const applyPreset = (label: string, days: number) => {
    setActivePreset(label);
    setDateRange([dayjs().subtract(days, 'days'), dayjs()]);
  };

  const handleRangeChange = (newRange: DateRange<Dayjs>) => {
    setActivePreset('');
    setDateRange(newRange);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          ShipBob Performance
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          3PL scorecard — fulfillment cost, on-time ship rate & split rate.
          Data syncs from ShipBob hourly.
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <ButtonGroup size="small" variant="outlined">
          {PRESETS.map(preset => (
            <Button
              key={preset.label}
              variant={activePreset === preset.label ? 'contained' : 'outlined'}
              onClick={() => applyPreset(preset.label, preset.days)}
            >
              {preset.label}
            </Button>
          ))}
        </ButtonGroup>
        <ControlledDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleRangeChange}
          label="Custom range"
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="shipbob-channel-label">Channel</InputLabel>
          <Select
            labelId="shipbob-channel-label"
            label="Channel"
            value={channelId}
            onChange={event => setChannelId(event.target.value)}
          >
            <MenuItem value="">All channels</MenuItem>
            {channels.map(channel => (
              <MenuItem key={channel.channelId} value={channel.channelId}>
                {channel.channelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={fetchData}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && data && (
        <>
          <SummaryCards
            totals={data.totals}
            speedOverall={data.shipSpeed?.overall}
          />
          <DailyTrendChart daily={data.daily} />
          {data.shipSpeed && <ShipSpeedTables shipSpeed={data.shipSpeed} />}
          {data.zones && (
            <ZonesTable zones={data.zones} zoneByFc={data.zoneByFc} />
          )}
          {isProductCostsLoading && (
            <Paper
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Loading product cost breakdown…
              </Typography>
            </Paper>
          )}
          {productCostsError && (
            <Alert severity="warning">
              Failed to load the product cost breakdown. Refresh to retry.
            </Alert>
          )}
          {productCosts && <ProductCostSection productCosts={productCosts} />}
        </>
      )}
    </Box>
  );
}
